import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Nav from "components/layout/Nav";
import { MenuBar } from "components/layout/MenuBar";
import { uploadProfilePhoto } from "api/profile";
import { setUser } from "Slices/authSlice";
import { showToast } from "components/shared/Toast";

const compressImage = (file, maxWidth = 600, quality = 0.8) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressed = new File([blob], file.name.replace(/\.\w+$/, ".webp"), {
                type: "image/webp",
              });
              resolve(compressed);
            } else {
              reject(new Error("Compression failed"));
            }
          },
          "image/webp",
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ProfilePage = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const profileImageUrl = useMemo(() => {
    if (!user?.profileImage?.url) return null;
    if (user.profileImage.url.startsWith("http")) return user.profileImage.url;
    return `${process.env.REACT_APP_BACKEND_URL || "http://localhost:1052"}${user.profileImage.url}`;
  }, [user]);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const compressed = await compressImage(file);
      const updatedUser = await uploadProfilePhoto(compressed);
      dispatch(setUser(updatedUser));
      showToast("Profile image updated.", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to update profile image.", "error");
    } finally {
      setUploading(false);
    }
  };

  const triggerFile = () => fileInputRef.current?.click();

  return (
    <>
      <Nav />
      <div className="row" style={{ minHeight: "100vh", background: "#f5f7fb" }}>
        <MenuBar />
        <main style={{ width: "calc(100vw - 240px)", padding: "32px" }}>
          <div style={{ maxWidth: "720px", width: "100%" }} className="card">
            <div style={{ padding: "24px 24px 12px" }}>
              <h2 style={{ marginBottom: "8px" }}>Profile</h2>
              <p style={{ color: "#64748b", marginBottom: "20px" }}>Your account details and activity.</p>
              <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
                <div
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "24px",
                    overflow: "hidden",
                    border: "2px solid #e2e8f0",
                    background: "#0f172a",
                    display: "grid",
                    placeItems: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "28px",
                  }}
                >
                {profileImageUrl ? (
                  <img src={profileImageUrl} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span>{(user?.name || user?.id || "?").slice(0, 2).toUpperCase()}</span>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <button className="btn-blue" onClick={triggerFile} disabled={uploading} style={{ width: "180px" }}>
                  {uploading ? "Uploading..." : "Change photo"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </div>
            </div>
            </div>

            <div style={{ padding: "0 24px 24px", display: "grid", rowGap: "12px" }}>
              <InfoRow label="Name" value={user?.name || user?.id} />
              <InfoRow label="Role" value={user?.role || (user?.isSuperAdmin ? "superAdmin" : "N/A")} />
              <InfoRow label="Login ID" value={user?.id} />
              <InfoRow label="Segments" value={renderList(user?.access?.segments, "All segments")} />
              <InfoRow label="Locations" value={renderList(user?.access?.locations, "All locations")} />
              <InfoRow label="Contact" value={user?.contactNumber || "—"} />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

const renderList = (value, fallback) => {
  if (!value) return "—";
  if (value === "all") return fallback;
  if (Array.isArray(value) && value.length) return value.join(", ");
  return fallback;
};

const InfoRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: "10px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
    <span style={{ color: "#475569", fontWeight: 600 }}>{label}</span>
    <span style={{ color: "#0f172a", fontWeight: 600, textAlign: "right", marginLeft: "16px" }}>{value}</span>
  </div>
);

export default ProfilePage;
